/**
 * Org Admin Portal sidebar — visible to org_admin users.
 * Shows: Policy Templates, AI Skills.
 */
import { FileText, Shield, Sparkles } from 'lucide-react';
import React from 'react';
import { WorkspaceSwitcher } from '@header/WorkspaceSwitcher';
import { cn } from '@foundation/lib/utils';

export type OrgAdminTab = 'policy-templates' | 'ai-skills';

type OrgAdminSidebarProps = {
  activeTab: OrgAdminTab;
  onTabChange: (tab: OrgAdminTab) => void;
};

const TABS: { id: OrgAdminTab; label: string; icon: React.ElementType; description: string }[] = [
  {
    id: 'policy-templates',
    label: 'Policy Templates',
    icon: FileText,
    description: 'Manage and organise policy templates',
  },
  {
    id: 'ai-skills',
    label: 'AI Skills',
    icon: Sparkles,
    description: 'Configure AI generation skills',
  },
];

export const OrgAdminSidebar: React.FC<OrgAdminSidebarProps> = ({
  activeTab,
  onTabChange,
}) => (
  <div className="w-64 bg-card border-r border-border h-full">
    <div className="pt-3">
      <WorkspaceSwitcher />
    </div>

    <div className="px-6 pb-6">
      <div className="flex items-center gap-2 mb-6">
        <Shield className="h-6 w-6 text-primary" />
        <h2 className="text-lg font-semibold">Admin</h2>
      </div>

      <nav className="space-y-2">
        {TABS.map(tab => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => onTabChange(tab.id)}
              className={cn(
                'w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors',
                isActive
                  ? 'bg-primary text-primary-foreground'
                  : 'hover:bg-muted text-muted-foreground hover:text-foreground'
              )}
            >
              <Icon className="h-5 w-5" />
              <div className="flex-1">
                <div className="font-medium">{tab.label}</div>
                <div
                  className={cn(
                    'text-xs',
                    isActive ? 'text-primary-foreground/70' : 'text-muted-foreground'
                  )}
                >
                  {tab.description}
                </div>
              </div>
            </button>
          );
        })}
      </nav>
    </div>
  </div>
);
